import React from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Menu,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { http, unwrapApiResponse } from '../api/http';
import { PageHeader } from '../components/PageHeader';
import { ErrorBanner } from '../components/ErrorBanner';
import { RecordDialog } from '../components/RecordDialog';
import { UserPremiumDialog } from '../components/UserPremiumDialog';
import { useAuth } from '../auth/AuthContext';
import { useAppUi } from '../app/useAppUi';
import { formatDateTime } from '../utils/date';
import MoreVertIcon from '@mui/icons-material/MoreVert';

export function BotUsersPage() {
  const { t } = useAppUi();
  const { admin } = useAuth();
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);
  const [rows, setRows] = React.useState([]);
  const [rowCount, setRowCount] = React.useState(0);

  const [page, setPage] = React.useState(0);
  const [pageSize, setPageSize] = React.useState(20);
  const [search, setSearch] = React.useState('');

  const canWrite = admin?.role === 'SUPER_ADMIN' || admin?.role === 'ADMIN';
  const isSuper = admin?.role === 'SUPER_ADMIN';
  const [viewOpen, setViewOpen] = React.useState(false);
  const [premiumOpen, setPremiumOpen] = React.useState(false);
  const [selected, setSelected] = React.useState(null);
  const [savingPremium, setSavingPremium] = React.useState(false);
  const [deleteOpen, setDeleteOpen] = React.useState(false);
  const [deleting, setDeleting] = React.useState(false);

  const fetchData = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await http.get('/admin/bot-users', {
        params: { take: pageSize, page: page + 1, search: search || undefined },
      });
      const data = unwrapApiResponse(res);
      setRows(data?.users || []);
      setRowCount(data?.total || 0);
    } catch (e) {
      setError(e);
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, search]);

  React.useEffect(() => {
    fetchData();
  }, [fetchData]);

  const viewUser = async (row) => {
    setError(null);
    try {
      const res = await http.get(`/admin/bot-users/${row.id}`);
      const data = unwrapApiResponse(res);
      setSelected(data);
      setViewOpen(true);
    } catch (e) {
      setError(e);
    }
  };

  const openPremium = (row) => {
    setSelected(row);
    setPremiumOpen(true);
  };

  const openDelete = (row) => {
    setSelected(row);
    setDeleteOpen(true);
  };

  function BotUserActionsCell({ row }) {
    const [anchorEl, setAnchorEl] = React.useState(null);
    const open = Boolean(anchorEl);

    const close = () => setAnchorEl(null);

    const onAction = async (fn) => {
      close();
      await fn();
    };

    return (
      <>
        <IconButton
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            setAnchorEl(e.currentTarget);
          }}
          aria-label={t('botUsers.colActions')}
        >
          <MoreVertIcon fontSize="small" />
        </IconButton>
        <Menu
          anchorEl={anchorEl}
          open={open}
          onClose={(e) => {
            e?.stopPropagation?.();
            close();
          }}
          onClick={(e) => e.stopPropagation()}
          slotProps={{
            paper: {
              elevation: 0,
              sx: {
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1.25,
                minWidth: 200,
              },
            },
          }}
        >
          <MenuItem onClick={() => onAction(() => viewUser(row))}>{t('common.view')}</MenuItem>
          <MenuItem disabled={!canWrite} onClick={() => onAction(() => openPremium(row))}>
            {t('common.premium')}
          </MenuItem>
          {isSuper ? (
            <MenuItem
              onClick={() => onAction(() => openDelete(row))}
              sx={{ color: 'error.main' }}
            >
              {t('common.delete')}
            </MenuItem>
          ) : null}
        </Menu>
      </>
    );
  }

  const savePremium = async (patch) => {
    if (!selected?.id) return;
    setSavingPremium(true);
    setError(null);
    try {
      await http.put(`/admin/users/${selected.id}/premium`, patch);
      setPremiumOpen(false);
      await fetchData();
    } catch (e) {
      setError(e);
    } finally {
      setSavingPremium(false);
    }
  };

  const deleteBotUser = async () => {
    if (!selected?.id) return;
    setDeleting(true);
    setError(null);
    try {
      await http.delete(`/admin/bot-users/${selected.id}`);
      setDeleteOpen(false);
      await fetchData();
    } catch (e) {
      setError(e);
    } finally {
      setDeleting(false);
    }
  };

  const columns = React.useMemo(
    () => [
      { field: 'id', headerName: t('botUsers.colId'), width: 80 },
      { field: 'name', headerName: t('botUsers.colName'), flex: 1, minWidth: 200 },
      { field: 'phone_number', headerName: t('botUsers.colPhone'), width: 160 },
      { field: 'chat_id', headerName: t('botUsers.colChatId'), width: 140 },
      { field: 'bot_lang', headerName: t('botUsers.colLang'), width: 100 },
      { field: 'bot_step', headerName: t('botUsers.colStep'), width: 160 },
      { field: 'created_at', headerName: t('botUsers.colCreated'), width: 150, valueFormatter: (v) => formatDateTime(v) },
      {
        field: 'actions',
        headerName: t('botUsers.colActions'),
        width: 90,
        sortable: false,
        filterable: false,
        renderCell: (p) => <BotUserActionsCell row={p.row} />,
      },
    ],
    [t, canWrite, isSuper]
  );

  return (
    <Box>
      <PageHeader
        title={t('botUsers.title')}
        subtitle={t('botUsers.subtitle')}
        right={
          <Button variant="outlined" onClick={fetchData} disabled={loading}>
            {t('common.refresh')}
          </Button>
        }
      />

      <ErrorBanner error={error} />

      <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} sx={{ mb: 2 }}>
        <TextField
          label={t('botUsers.search')}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          size="small"
          sx={{ width: { xs: '100%', md: 360 } }}
        />
      </Stack>

      <Box sx={{ height: 640, bgcolor: 'background.paper', borderRadius: 2, boxShadow: (th) => th.custom.shadow, overflow: 'hidden' }}>
        <DataGrid
          rows={rows}
          // Technical columns stay available from the column menu.
          initialState={{ columns: { columnVisibilityModel: { chat_id: false, bot_step: false } } }}
          columns={columns}
          loading={loading}
          paginationMode="server"
          rowCount={rowCount}
          density="compact"
          pageSizeOptions={[10, 20, 50, 100]}
          paginationModel={{ page, pageSize }}
          onPaginationModelChange={(m) => {
            setPage(m.page);
            setPageSize(m.pageSize);
          }}
          disableRowSelectionOnClick
        />
      </Box>

      <RecordDialog
        open={viewOpen}
        title={t('botUsers.dialogTitle', { id: selected?.id || '' })}
        record={selected}
        onClose={() => setViewOpen(false)}
      />

      <UserPremiumDialog
        open={premiumOpen}
        user={selected}
        saving={savingPremium}
        onClose={() => setPremiumOpen(false)}
        onSave={savePremium}
      />

      <Dialog open={deleteOpen} onClose={() => setDeleteOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>{t('botUsers.deleteTitle')}</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary">
            {t('botUsers.deleteConfirm', { id: selected?.id ?? '' })}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteOpen(false)} disabled={deleting}>
            {t('common.cancel')}
          </Button>
          <Button variant="contained" color="error" onClick={deleteBotUser} disabled={deleting}>
            {deleting ? t('common.saving') : t('common.confirm')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

